import { useState, useEffect } from 'react';
import Wrapper from '../assets/wrappers/DashboardFormPage';
import {
  useOutletContext,
  redirect,
  useParams,
  useNavigate,
} from 'react-router-dom';
import { useLoaderData } from 'react-router-dom';
import { MediaContainer } from '../components/Media';
import { toast } from 'react-toastify';
import { customFetch } from '../utils/custom-fetch.js';

export const loader = async ({ params }) => {
  try {
    const { data } = await customFetch(`/buildings/${params.id}`);
    return data;
  } catch (error) {
    toast.error(error.response?.data?.msg);
    return redirect('/dashboard/all-buildings');
  }
};

// Required by App.jsx route config; form now submits manually via axios
export const action = async () => null;

const MAX_MEDIA = 10;

const AddMediaBuilding = () => {
  const { id } = useParams();
  const { building } = useLoaderData();
  const { media: initialMedia } = useLoaderData();
  const { user } = useOutletContext();
  const navigate = useNavigate();

  const [media, setMedia] = useState(initialMedia || []);
  const [uploadProgress, setUploadProgress] = useState(null); // null = idle, 0-100 = uploading
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (!id) navigate('/dashboard/all-buildings');
  }, [id, navigate]);

  const atLimit = media.length >= MAX_MEDIA;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 150 * 1024 * 1024) {
      toast.error('גודל הקובץ חורג מהמותר (150MB)');
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('יש לבחור קובץ');
      return;
    }

    const formData = new FormData();
    formData.append('media', selectedFile);
    formData.append('buildingIds', id);
    formData.append('isPublic', false);

    setUploadProgress(0);

    try {
      await customFetch.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(pct);
        },
      });

      toast.success('המדיה עלתה בהצלחה');
      setSelectedFile(null);
      setUploadProgress(null);

      // Refresh media list
      const { data } = await customFetch(`/buildings/${id}`);
      setMedia(data.media || []);
    } catch (error) {
      setUploadProgress(null);
      toast.error(error?.response?.data?.msg || 'שגיאה בהעלאת המדיה');
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    try {
      await customFetch.delete(`/media/${mediaId}`);
      toast.success('המדיה נמחקה בהצלחה');
      setMedia((prev) => prev.filter((m) => m._id !== mediaId));
    } catch (error) {
      toast.error('שגיאה במחיקת המדיה');
    }
  };

  return (
    <>
      <Wrapper title='הוספת מדיה לבניין'>
        <form className='form' onSubmit={handleSubmit}>
          <h4 className='form-title'>
            הוספת מדיה ל{building.street} {building.houseNumber} , {building.city}
            {` (${media.length}/${MAX_MEDIA} קבצים)`}
          </h4>

          {atLimit ? (
            <p style={{ color: 'var(--red-dark)', textAlign: 'center', margin: '1rem 0' }}>
              הגעת למקסימום 10 מדיות לבניין זה. מחק קובץ קיים כדי להעלות חדש.
            </p>
          ) : (
            <div className='form-center'>
              <div className='form-row'>
                <label htmlFor='media' className='form-label'>
                  בחר תמונה או סרטון (עד 150MB)
                </label>
                <input
                  type='file'
                  id='media'
                  name='media'
                  className='form-input'
                  accept='image/*,video/mp4,video/quicktime,video/x-msvideo,video/webm'
                  onChange={handleFileChange}
                  disabled={uploadProgress !== null}
                />
              </div>

              {uploadProgress !== null && (
                <div style={{ width: '100%', margin: '1rem 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.85rem' }}>
                    <span>{uploadProgress < 100 ? 'מעלה...' : 'מעבד...'}</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div style={{ width: '100%', height: '10px', background: 'var(--grey-100)', borderRadius: '5px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${uploadProgress}%`,
                        background: 'var(--primary-500)',
                        borderRadius: '5px',
                        transition: 'width 0.2s ease',
                      }}
                    />
                  </div>
                  {uploadProgress === 100 && (
                    <p style={{ fontSize: '0.8rem', color: 'var(--grey-500)', marginTop: '4px', textAlign: 'center' }}>
                      הקובץ הועלה — ממתין לאישור השרת...
                    </p>
                  )}
                </div>
              )}

              <button
                type='submit'
                className='btn btn-block'
                disabled={uploadProgress !== null || !selectedFile}
              >
                {uploadProgress !== null ? `מעלה... ${uploadProgress}%` : 'שמירה'}
              </button>
            </div>
          )}
        </form>
      </Wrapper>

      <MediaContainer media={media} onDelete={handleDeleteMedia} />
    </>
  );
};

export default AddMediaBuilding;
